import { AuthService } from 'src/app/shared/authentication/auth.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { BehaviorSubject, catchError, finalize, tap, throwError } from 'rxjs';
import { AdminsApi, AdminSummary } from 'src/app/hickory-api';
import { BaseDataSource, TableColumn } from './datasource';
import { FilterField } from '../../filtered-search/filtered-search.component';

export interface ListAdminsRequest {
  page?: number;
  pageSize?: number;
  sort?: string;
  ids?: number[];
  name?: string;
  username?: string;
  email?: string;
  group?: string;
  role?: string;
  tenantId?: number;
  tenantName?: string;
  state?: string;
  credentialIds?: number[];
  externalIds?: string[];
}

export class AdminsDataSource extends BaseDataSource<AdminSummary, AdminsApi, ListAdminsRequest> {
  protected columns: TableColumn<AdminSummary>[] = AdminsTableColumns.columns;

  get availableColumns(): TableColumn<AdminSummary>[] {
    return AdminsTableColumns.getAvailableColumns(this.authService);
  }

  get queryArguments(): object {
    const request = this.currentRequest.value;

    return {
      page: request.page,
      pageSize: request.pageSize,
      sort: request.sort ? request.sort : null,
      search: request.name ? request.name : null,
      username: request.username,
      email: request.email,
      groups: request.group,
      role: request.role,
      tenantId: request.tenantId,
      tenantName: request.tenantName,
      state: request.state,
      credentialsId: request.credentialIds?.length ? request.credentialIds[0] : null,
    };
  }

  idsSearchTerm$ = new BehaviorSubject<number[]>(null);
  usernameSearchTerm$ = new BehaviorSubject<string>(null);
  emailSearchTerm$ = new BehaviorSubject<string>(null);
  groupSearchTerm$ = new BehaviorSubject<string>(null);
  roleSearchTerm$ = new BehaviorSubject<string>(null);
  tenantIdSearchTerm$ = new BehaviorSubject<number>(null);
  tenantNameSearchTerm$ = new BehaviorSubject<string>(null);
  stateSearchTerm$ = new BehaviorSubject<string>(null);
  credentialIdsSearchTerm$ = new BehaviorSubject<number[]>(null);
  externalIdsSearchTerm$ = new BehaviorSubject<string[]>(null);

  previousRequest: ListAdminsRequest;

  constructor(
    service: AdminsApi,
    protected authService: AuthService,
    paginator: MatPaginator,
    sort: MatSort,
    request: object,
    loadOnInit: boolean = false
  ) {
    super(service, paginator, sort, request, loadOnInit);
  }

  override init() {
    super.init(false);

    this.subscriptions.add(this.createSubscription(this.idsSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.usernameSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.emailSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.groupSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.roleSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.tenantIdSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.tenantNameSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.stateSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.credentialIdsSearchTerm$));
    this.subscriptions.add(this.createSubscription(this.externalIdsSearchTerm$));

    this.hasInit = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getArguments(request: ListAdminsRequest): any[] {
    return [
      request.page,
      request.pageSize,
      request.sort,
      request.ids,
      request.name,
      request.username,
      request.email,
      request.group,
      request.role,
      request.tenantId,
      request.tenantName,
      request.state,
      request.credentialIds,
      request.externalIds,
    ];
  }

  public override loadData(forceRefresh: boolean = false) {
    if (!this.hasInit) {
      return;
    }

    const currentRequest = this.currentRequest.value ?? {};

    currentRequest.sort = this.sortDirection + this.sortField;
    currentRequest.page = this.pageIndex + 1;
    currentRequest.pageSize = this.pageSize;
    currentRequest.ids = this.idsSearchTerm$.value;
    currentRequest.name = this.searchTerm$.value;
    currentRequest.username = this.usernameSearchTerm$.value;
    currentRequest.email = this.emailSearchTerm$.value;
    currentRequest.group = this.groupSearchTerm$.value;
    currentRequest.role = this.roleSearchTerm$.value;
    currentRequest.tenantId = this.tenantIdSearchTerm$.value;
    currentRequest.tenantName = this.tenantNameSearchTerm$.value;
    currentRequest.state = this.stateSearchTerm$.value;
    currentRequest.credentialIds = this.credentialIdsSearchTerm$.value;
    currentRequest.externalIds = this.externalIdsSearchTerm$.value;

    this.currentRequest.next(currentRequest);

    if (
      this.previousRequest &&
      !forceRefresh &&
      !this.hasError.value &&
      this.isEqual(this.previousRequest, currentRequest)
    ) {
      return;
    }

    this.hasError.next(false);
    this.errorMessage.next(null);
    this.loadingSubject.next(true);
    this.previousRequest = { ...currentRequest };

    this.service
      .getAdminUsers(...this.getArguments(currentRequest))
      .pipe(
        catchError((err) => {
          this.hasError.next(true);
          this.errorMessage.next('Admins.Table.ErrorMessage');
          this.dataSubject.next([]);
          return throwError(() => err);
        }),
        finalize(() => this.loadingSubject.next(false)),
        tap((response) => {
          this.itemCount = response.totalItemCount;
          this.dataSubject.next(response.items);
        })
      )
      .subscribe();
  }

  isEqual(a: ListAdminsRequest, b: ListAdminsRequest): boolean {
    return (
      a.page == b.page &&
      a.pageSize == b.pageSize &&
      a.sort == b.sort &&
      a.name == b.name &&
      a.username == b.username &&
      a.email == b.email &&
      a.group == b.group &&
      a.role == b.role &&
      a.tenantId == b.tenantId &&
      a.tenantName == b.tenantName &&
      a.state == b.state &&
      a.ids == b.ids &&
      a.credentialIds == b.credentialIds &&
      a.externalIds == b.externalIds
    );
  }
}

export class AdminsTableColumns {
  public static readonly columns: TableColumn<AdminSummary>[] = [
    {
      field: 'lastName, firstName',
      localizationKey: 'Admins.Table.Columns.Name',
      style: { 'min-width': '15rem', padding: '0 0.5rem' },
      render: (admin: AdminSummary) => {
        return `${admin.lastName}, ${admin.firstName}`;
      },
      link: (admin: AdminSummary, queryParams = null, _ = null) => {
        return {
          route: ['/admins', admin.id],
          queryParams: queryParams,
        };
      },
      sticky: true,
    },
    {
      field: 'username',
      localizationKey: 'Admins.Table.Columns.Username',
      filterable: true,
      style: { 'min-width': '25rem' },
    },
    {
      field: 'email',
      localizationKey: 'Admins.Table.Columns.Email',
      filterable: true,
      style: { 'min-width': '25rem' },
    },
    {
      field: 'groups',
      localizationKey: 'Admins.Table.Columns.Groups',
      render: (admin: AdminSummary) => {
        return admin.groups?.map((x) => x.name)?.join(', ');
      },
      filterable: true,
      noSort: true,
    },
    {
      field: 'role',
      localizationKey: 'Admins.Table.Columns.Role',
      filterable: true,
    },
    {
      field: 'identity',
      localizationKey: 'Identity Type',
      filterable: false,
      noSort: true,
      render(_row) {
        return undefined;
      },
      icon(row: AdminSummary) {
        if (row.credentialsId) {
          return 'badge';
        }

        return 'warning';
      },
      iconTooltip(row: AdminSummary) {
        if (row.credentialsId) {
          return 'Credential';
        }

        return 'Admins.Table.Warning';
      },
      style: { 'max-width': '4rem', 'text-align': 'center' },
    },
  ];

  public static readonly adminColumns: TableColumn<AdminSummary>[] = [
    {
      field: 'credentialsId',
      localizationKey: 'Admins.Table.Columns.CredentialsId',
      filterable: true,
      noSort: true,
    },
    {
      field: 'state',
      localizationKey: 'Admins.Table.Columns.State',
      filterable: true,
    },
    {
      field: 'tenantId',
      localizationKey: 'Admins.Table.Columns.TenantId',
      filterable: true,
    },
    {
      field: 'tenantName',
      localizationKey: 'Admins.Table.Columns.TenantName',
      filterable: true,
    },
  ];

  public static getAvailableColumns(authService: AuthService): TableColumn<AdminSummary>[] {
    let columns = this.columns;

    if (authService.isSuperAdmin()) {
      columns = columns.concat(this.adminColumns);
    }
    return columns;
  }

  public static columnsToFilterFields(columns: TableColumn<AdminSummary>[], required: string[] = null): FilterField[] {
    return columns
      .filter((f) => f.filterable)
      .map((f) => {
        return { field: f.field, localizedLabel: f.localizationKey, type: 'text', required: required?.some(x => x === f.field) };
      });
  }

  public static getAvailableFilterFields(authService: AuthService, required: string[] = null): FilterField[] {
    const columns = this.getAvailableColumns(authService);
    return this.columnsToFilterFields(columns, required);
  }
}
